import { useMemo, useState, useEffect } from 'react'

import { BOT_USERNAME } from '@/lib/telegramLinks'

/**
 * Are we actually running INSIDE Telegram (Mini App)? On regular browsers
 * the answer is no and the Telegram SDK is never loaded — telegram.org is
 * unreachable from many RU networks and must not block the page.
 */
export function isTelegramEnvironment(): boolean {
  if (typeof window === 'undefined') return false
  if (window.Telegram?.WebApp) return true
  return /Telegram/i.test(navigator.userAgent) || window.location.hash.includes('tgWebAppData')
}

let telegramScriptPromise: Promise<void> | null = null

/** Inject the Telegram WebApp SDK lazily (non-blocking). */
export function loadTelegramWebApp(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.Telegram?.WebApp) return Promise.resolve()
  if (!telegramScriptPromise) {
    telegramScriptPromise = new Promise<void>((resolve, reject) => {
      const s = document.createElement('script')
      s.src = 'https://telegram.org/js/telegram-web-app.js'
      s.async = true
      s.onload = () => resolve()
      s.onerror = () => reject(new Error('telegram-web-app.js failed to load'))
      document.head.appendChild(s)
    }).catch((err: unknown) => {
      // Allow retry on a later navigation attempt.
      telegramScriptPromise = null
      throw err
    })
  }
  return telegramScriptPromise as Promise<void>
}

interface TelegramContext {
  initData: string
  isTelegramMiniApp: boolean
  telegramUserId: string | undefined
  telegramUsername: string | undefined
  telegramFirstName: string | undefined
  openInBot: () => void
  openPaymentUrl: (url: string) => void
  openExternalUrl: (url: string) => void
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string
        initDataUnsafe: {
          user?: {
            id: number
            username?: string
            first_name?: string
          }
        }
        ready(): void
        expand(): void
        setHeaderColor(color: string): void
        setBackgroundColor(color: string): void
        openLink(url: string, options?: { try_instant_view?: boolean }): void
        BackButton: {
          show(): void
          hide(): void
          onClick(callback: () => void): void
          offClick(callback: () => void): void
        }
        MainButton: {
          text: string
          color: string
          textColor: string
          show(): void
          hide(): void
          onClick(callback: () => void): void
          offClick(callback: () => void): void
          setParams(params: { text?: string; color?: string; text_color?: string }): void
        }
        viewport: {
          height: number
          expand(): void
        }
      }
    }
  }
}

export function useTelegram(): TelegramContext {
  // Bump when the lazily-loaded SDK becomes available, so consumers re-read
  // initData / user info instead of caching the pre-script state forever.
  const [sdkTick, setSdkTick] = useState(0)

  useEffect(() => {
    if (!isTelegramEnvironment() || window.Telegram?.WebApp) return
    let alive = true
    loadTelegramWebApp()
      .then(() => {
        if (alive) initTelegramWebApp() // ready()/expand() once the SDK exists
      })
      .catch(() => {}) // blocked/unreachable → the user simply isn't auto-logged-in
      .finally(() => {
        if (alive) setSdkTick(t => t + 1)
      })
    return () => {
      alive = false
    }
  }, [])

  return useMemo(() => {
    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined

    const isTelegramMiniApp =
      typeof tg !== 'undefined' &&
      typeof tg.initData === 'string' &&
      tg.initData.length > 0

    const user = isTelegramMiniApp ? tg?.initDataUnsafe?.user : undefined

    const telegramUserId = user?.id ? String(user.id) : undefined
    const telegramUsername = user?.username as string | undefined
    const telegramFirstName = user?.first_name as string | undefined

    const openExternalUrl = (url: string) => {
      if (isTelegramMiniApp && tg?.openLink) {
        tg.openLink(url, { try_instant_view: false })
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    }

    const openInBot = () => {
      const path = window.location.pathname + window.location.search
      const startParam = encodeURIComponent(path.replace(/^\//, '').replace(/[^a-zA-Z0-9_-]/g, '_'))
      const botUrl = startParam
        ? `https://t.me/${BOT_USERNAME}?start=${startParam}`
        : `https://t.me/${BOT_USERNAME}`
      openExternalUrl(botUrl)
    }

    const openPaymentUrl = (url: string) => {
      if (isTelegramMiniApp && tg?.openLink) {
        tg.openLink(url, { try_instant_view: false })
      } else {
        window.location.href = url
      }
    }

    return {
      initData: isTelegramMiniApp ? (tg?.initData ?? '') : '',
      isTelegramMiniApp,
      telegramUserId,
      telegramUsername,
      telegramFirstName,
      openInBot,
      openPaymentUrl,
      openExternalUrl,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkTick])
}

export function initTelegramWebApp() {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
    const tg = window.Telegram.WebApp
    tg.ready()
    tg.expand()
    // Use Telegram theme params for colors if available
    // backgroundColor and headerColor are optional theme params on WebApp
    const bgColor = (tg as any).backgroundColor || '#0F1419'
    const headerColor = (tg as any).headerColor || bgColor
    tg.setHeaderColor(headerColor)
    tg.setBackgroundColor(bgColor)
  }
}
